import { useEffect, useState } from 'react';
import { getPetSitterData, getRoutines, getMeds, getPetCard } from './api';
import RecentTreatmentsBlock from './RecentTreatmentsBlock';

const ROUTINE_REPEAT_LABELS = {
  daily:   'ежедневно',
  alt_day: 'через день',
  every3d: 'через 2 дня',
  week:    'раз в неделю',
};

export default function PetSitterBlock({ petId, standalone = false, docs = {}, onDocsClick = null }) {
  const [data, setData] = useState(null);
  const [cardData, setCardData] = useState(null);
  const [routines, setRoutines] = useState([]);
  const [meds, setMeds] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      getPetSitterData(petId),
      getRoutines(petId),
      getMeds(petId),
      getPetCard(petId),
    ])
      .then(([d, r, m, c]) => { setData(d); setRoutines(r); setMeds(m); setCardData(c); })
      .catch(() => { setData(null); setRoutines([]); setMeds([]); setCardData(null); })
      .finally(() => setLoading(false));
  }, [petId]);

  if (loading) return null;

  const petsitterDocs = docs?.petsitter || [];

  const waterRecord = data?.feeding.find((f) => f.food_name === '💧 Вода');
  const foodRecords = data?.feeding.filter((f) => f.food_name !== '💧 Вода') || [];

  const observeEpisodes = data?.episodes.filter((e) => e.danger_level === 'Наблюдаем') || [];
  const doctorEpisodes = data?.episodes.filter((e) => e.danger_level === 'Едем к врачу') || [];

  const isEmpty =
    !data ||
    (!foodRecords.length && !data.care.length &&
     !data.contacts.length && !data.notes.length &&
     !data.supplementation.length && !data.diagnoses.length &&
     !data.episodes.length && !routines.length && !meds.length && !petsitterDocs.length && !waterRecord);

  return (
    <section className={standalone ? 'ps-standalone' : 'card-section'} id="section-petsitter">
      {!standalone && (
        <div className="ps-header">
          <span className="ps-title">🤝 Для пет ситтера</span>
        </div>
      )}

      {isEmpty ? (
        <p style={{ color: '#888', fontSize: 18, margin: 0 }}>
          Информация ещё не добавлена
        </p>
      ) : (
        <>
          {/* ВАЖНАЯ ИНФОРМАЦИЯ ДОМА */}
          <div style={{ marginTop: 24, marginBottom: 20 }}>
            <div style={{ fontSize: 20, fontWeight: 'bold', color: '#8B3A3A', paddingLeft: 8, letterSpacing: '0.5px' }}>
              🏠 Важная информация дома
            </div>
          </div>

          {/* Питание */}
          {foodRecords.length > 0 && (
            <div className="ps-block">
              <div className="ps-block-title">🥣 Питание</div>
              {foodRecords.map((f) => (
                <div key={f.id} className="ps-row">
                  {f.ration_products && f.ration_products.length > 0 ? (
                    <div className="ps-ration">
                      <div className="ps-ration-name">🍖 {f.food_name}</div>
                      <div className="ps-ration-items">
                        {f.ration_products.map((p, i) => (
                          <div key={i} className="ps-ration-item">
                            • {p.product_name} — {p.amount} г
                          </div>
                        ))}
                      </div>
                      <div className="ps-ration-total">
                        Итого: {f.ration_products.reduce((s, p) => s + p.amount, 0).toFixed(1)} г
                      </div>
                    </div>
                  ) : (
                    <div className="ps-row-main">{f.food_name}</div>
                  )}
                  {f.schedule && <div className="ps-row-sub">⏰ {f.schedule}</div>}
                  {f.feeding_notes && <div className="ps-row-sub">📝 {f.feeding_notes}</div>}
                  {f.treats && <div className="ps-row-sub">🦴 {f.treats}</div>}
                </div>
              ))}
            </div>
          )}

          {/* Вода */}
          {waterRecord && (
            <div className="ps-block">
              <div className="ps-block-title">💧 Вода</div>
              {waterRecord.feeding_notes && (
                <div className="ps-row">
                  <div className="ps-row-sub">{waterRecord.feeding_notes}</div>
                </div>
              )}
            </div>
          )}

          {/* Сапплементация */}
          {data.supplementation.length > 0 && (
            <div className="ps-block">
              <div className="ps-block-title">🌿 Сапплементация</div>
              {data.supplementation.map((s) => (
                <div key={s.id} className="ps-row">
                  <div className="ps-row-main">{s.name}</div>
                  {s.amount && <div className="ps-row-sub">📏 {s.amount}</div>}
                  {s.frequency && <div className="ps-row-sub">🔁 {s.frequency}</div>}
                  {s.how_to_give && <div className="ps-row-sub">🍽️ {s.how_to_give}</div>}
                </div>
              ))}
            </div>
          )}

          {/* Уход */}
          {data.care.length > 0 && (
            <div className="ps-block">
              <div className="ps-block-title">🛁 Уход</div>
              {data.care.map((c) => (
                <div key={c.id} className="ps-row">
                  <div className="ps-row-main">{c.care_type}</div>
                  {c.frequency && <div className="ps-row-sub">📅 {c.frequency}</div>}
                </div>
              ))}
            </div>
          )}

          {/* Эпизоды - Наблюдаем */}
          {observeEpisodes.length > 0 && (
            <div className="ps-block">
              <div className="ps-block-title">📍 Эпизоды: Наблюдаем</div>
              {observeEpisodes.map((e) => (
                <div key={e.id} className="ps-row">
                  <div className="ps-row-main">{e.name}</div>
                  {e.contact && <div className="ps-row-sub">📱 {e.contact}</div>}
                  {e.algorithm && <div className="ps-row-sub">📋 {e.algorithm}</div>}
                </div>
              ))}
            </div>
          )}

          {/* Эпизоды - Едем к врачу */}
          {doctorEpisodes.length > 0 && (
            <div className="ps-block" style={{ borderLeft: '4px solid #ff4444', backgroundColor: '#fff5f5' }}>
              <div className="ps-block-title" style={{ color: '#d32f2f', fontWeight: 'bold', fontSize: '18px' }}>
                🚑 ЭПИЗОДЫ: ЕДЕМ К ВРАЧУ
              </div>
              {doctorEpisodes.map((e) => (
                <div key={e.id} className="ps-row">
                  <div className="ps-row-main">{e.name}</div>
                  {e.contact && <div className="ps-row-sub">📱 {e.contact}</div>}
                  {e.algorithm && <div className="ps-row-sub">📋 {e.algorithm}</div>}
                </div>
              ))}
            </div>
          )}

          {/* Контакты */}
          {data.contacts.length > 0 && (
            <div className="ps-block">
              <div className="ps-block-title">📞 Контакты</div>
              {data.contacts.map((c) => (
                <div key={c.id} className="ps-row">
                  <div className="ps-row-main">{c.name}</div>
                  {c.phone && (
                    <div className="ps-row-sub">
                      <span
                        className="ps-copyable"
                        onClick={() => navigator.clipboard?.writeText(c.phone)}
                      >
                        📱 {c.phone}
                      </span>
                    </div>
                  )}
                  {c.note && <div className="ps-row-sub">💬 {c.note}</div>}
                </div>
              ))}
            </div>
          )}

          {/* Важно */}
          {data.notes.length > 0 && (
            <div className="ps-block">
              <div className="ps-block-title">❗ Важно</div>
              {data.notes.map((n) => (
                <div key={n.id} className="ps-qa-row">
                  <div className="ps-qa-question">❓ {n.question}</div>
                  <div className="ps-qa-answer">{n.answer}</div>
                </div>
              ))}
            </div>
          )}

          {/* ВАЖНАЯ ИНФОРМАЦИЯ У ВРАЧА */}
          <div style={{ marginTop: 28, marginBottom: 20 }}>
            <div style={{ fontSize: 20, fontWeight: 'bold', color: '#8B3A3A', paddingLeft: 8, letterSpacing: '0.5px' }}>
              🏥 Важная информация у врача
            </div>
          </div>

          {/* Основная информация */}
          <div className="ps-block" style={{ marginBottom: 16 }}>
            <div className="ps-block-title">🐾 Основная информация</div>
            {cardData && (
              <div style={{ padding: '12px 0' }}>
                {cardData.gender && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #eee' }}>
                    <span style={{ fontWeight: '500', color: '#666' }}>Пол</span>
                    <span>{cardData.gender}</span>
                  </div>
                )}
                {cardData.breed && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #eee' }}>
                    <span style={{ fontWeight: '500', color: '#666' }}>Порода</span>
                    <span>{cardData.breed}</span>
                  </div>
                )}
                {cardData.birth_date && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #eee' }}>
                    <span style={{ fontWeight: '500', color: '#666' }}>Дата рождения</span>
                    <span>{cardData.birth_date}</span>
                  </div>
                )}
                {cardData.age && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #eee' }}>
                    <span style={{ fontWeight: '500', color: '#666' }}>Возраст</span>
                    <span>{cardData.age}</span>
                  </div>
                )}
                {cardData.passport_number && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #eee' }}>
                    <span style={{ fontWeight: '500', color: '#666' }}>Паспорт</span>
                    <span>{cardData.passport_number}</span>
                  </div>
                )}
                {cardData.microchip_number && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #eee' }}>
                    <span style={{ fontWeight: '500', color: '#666' }}>Микрочип №</span>
                    <span>{cardData.microchip_number}</span>
                  </div>
                )}
                {cardData.microchip_date && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #eee' }}>
                    <span style={{ fontWeight: '500', color: '#666' }}>Дата микрочипа</span>
                    <span>{cardData.microchip_date}</span>
                  </div>
                )}
                {cardData.tag_number && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}>
                    <span style={{ fontWeight: '500', color: '#666' }}>Клеймо</span>
                    <span>{cardData.tag_number}</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Документы */}
          {petsitterDocs.length > 0 && (
            <div className="ps-block">
              <div className="ps-block-title">📎 Документы</div>
              {petsitterDocs.map((doc) => (
                <div key={doc.id} className="ps-row">
                  <button
                    className="ps-doc-link"
                    onClick={() => onDocsClick?.('petsitter')}
                    style={{ cursor: 'pointer', textAlign: 'left', flex: 1 }}
                  >
                    📄 {doc.original_name}
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Последние обработки */}
          {cardData && (
            <RecentTreatmentsBlock pet={cardData} standalone />
          )}

          {/* Лекарства */}
          {meds.length > 0 && (
            <div className="ps-block">
              <div className="ps-block-title">💊 Лекарства</div>
              <div className="ps-block-subtitle">данные за последние 6 мес.</div>
              {meds.map((m, i) => (
                <div key={i} className="ps-row ps-med-row">
                  <span className="ps-med-name">{m.medication}</span>
                  <span className="ps-med-dates">
                    {m.date_from === m.date_to ? m.date_from : `${m.date_from} — ${m.date_to}`}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* Диагнозы */}
          {data.diagnoses.length > 0 && (
            <div className="ps-block">
              <div className="ps-block-title">📋 Диагнозы</div>
              {data.diagnoses.map((d) => (
                <div key={d.id} className="ps-row">
                  <div className="ps-row-main">{d.name}</div>
                  {d.diagnosed_date && <div className="ps-row-sub">📅 {d.diagnosed_date}</div>}
                  {d.diagnosis_text && <div className="ps-row-sub">📝 {d.diagnosis_text}</div>}
                  {d.affects && <div className="ps-row-sub">🎯 {d.affects}</div>}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </section>
  );
}
